import { useEffect, useState } from 'react';
import { Modal, Select, Input, message } from 'antd';
import { useSnackbar } from 'notistack';
import { addSurvivorshipRuleByEntityId, updateSurvivorshipRuleById } from "../../../../api's/DataModelApi";

const strategyOptions = [
  { value: 'frequency', label: 'Frequency' },
  { value: 'oldest_value', label: 'Oldest Value' },
  { value: 'minimum_value', label: 'Minimum Value' },
  { value: 'maximum_value', label: 'Maximum Value' },
  { value: 'recency', label: 'Recency' },
];

const AddSurvivorshipModal = ({
  open,
  onClose,
  entityId,
  attributes = [],
  fetchSurvivorshipRules = () => {},
  selectedRule,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const initialState = {
    name: '',
    description: '',
    attribute: '',
    strategy: '',
    priority: '',
    fallback: '',
    params: '',
  };

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const handleReset = () => {
    setFormData(initialState);
    setErrors({});
  };

  const handleOk = async () => {
    const newErrors = {};

    if (!formData.name) newErrors.name = 'Survivorship name is required';
    if (!formData.description) newErrors.description = 'Description is required';
    if (!formData.attribute) newErrors.attribute = 'Attribute is required';
    if (!formData.strategy) newErrors.strategy = 'Strategy is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return message.error('Please fill all required fields');
    }

    const parsedParams = formData.params
      ? formData.params
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean)
      : [];

    const payload = {
      survivorshipRuleName: formData.name,
      survivorshipRuleDescription: formData.description,
      ruleCondition: {
        attributeName: formData.attribute,
        matchType: formData.strategy,
        params: parsedParams.length > 0 ? parsedParams : [],
      },
    };

    setLoading(true);
    try {
      let response;

      if (selectedRule?.id) {
        response = await updateSurvivorshipRuleById(selectedRule.id, payload);
      } else {
        response = await addSurvivorshipRuleByEntityId(entityId, payload);
      }

      if (response.status === 200) {
        enqueueSnackbar(selectedRule ? 'Survivorship rule updated!' : 'Survivorship rule saved!', {
          variant: 'success',
        });

        handleReset();
        onClose();
        fetchSurvivorshipRules();
      }
    } catch (error) {
      enqueueSnackbar('Failed to save survivorship rule!', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    handleReset();
    onClose();
  };

  useEffect(() => {
    if (selectedRule) {
      setFormData({
        name: selectedRule.survivorshipRuleName || '',
        description: selectedRule.survivorshipRuleDescription || '',
        attribute: selectedRule.ruleCondition?.attributeName || '',
        strategy: selectedRule.ruleCondition?.matchType || '',
        priority: '',
        fallback: '',
        params: selectedRule.ruleCondition?.params?.join(', ') || '',
      });
    } else {
      handleReset();
    }
  }, [selectedRule]);

  return (
    <Modal
      title="Add Survivorship Rule"
      open={open}
      centered
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={loading}
    >
      <div className="field-block">
        <label>
          Survivorship Name <span style={{ color: 'red' }}>*</span>
        </label>
        <Input
          placeholder="Enter survivorship name"
          value={formData.name}
          onChange={(e) => updateField('name', e.target.value)}
          status={errors.name ? 'error' : ''}
        />
        {errors.name && <div style={{ color: 'red', marginTop: 4 }}>{errors.name}</div>}
      </div>

      {/* Description */}
      <div className="field-block" style={{ marginTop: 15 }}>
        <label>
          Description <span style={{ color: 'red' }}>*</span>
        </label>
        <Input.TextArea
          rows={3}
          placeholder="Enter description"
          value={formData.description}
          onChange={(e) => updateField('description', e.target.value)}
          status={errors.description ? 'error' : ''}
        />
        {errors.description && <div style={{ color: 'red', marginTop: 4 }}>{errors.description}</div>}
      </div>

      <div className="field-block" style={{ marginTop: 15 }}>
        <label>
          Attribute <span style={{ color: 'red' }}>*</span>
        </label>
        <Select
          placeholder="Select attribute"
          style={{ width: '100%' }}
          value={formData.attribute}
          onChange={(value) => updateField('attribute', value)}
          status={errors.attribute ? 'error' : ''}
        >
          {attributes
            .filter((attr) => !attr.is_hidden)
            .map((attr) => (
              <Select.Option key={attr.name} value={attr.name}>
                {attr.name}
              </Select.Option>
            ))}
        </Select>
        {errors.attribute && <div style={{ color: 'red', marginTop: 4 }}>{errors.attribute}</div>}
      </div>

      <div className="field-block" style={{ marginTop: 15 }}>
        <label>
          Strategy <span style={{ color: 'red' }}>*</span>
        </label>
        <Select
          placeholder="Select strategy"
          style={{ width: '100%' }}
          value={formData.strategy}
          onChange={(value) => updateField('strategy', value)}
          status={errors.strategy ? 'error' : ''}
        >
          {strategyOptions.map((opt) => (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Option>
          ))}
        </Select>
        {errors.strategy && <div style={{ color: 'red', marginTop: 4 }}>{errors.strategy}</div>}
      </div>

      <div className="field-block" style={{ marginTop: 15 }}>
        <label>Params (comma separated)</label>
        <Input
          placeholder="E.g. value1, value2, value3"
          value={formData.params}
          onChange={(e) => updateField('params', e.target.value)}
        />
        <small style={{ color: '#888' }}>Enter values separated by comma.</small>
      </div>
    </Modal>
  );
};

export default AddSurvivorshipModal;
